// src/new/components/monitor/TriageChat/useExpressChatSSE.ts
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createParser } from 'eventsource-parser';
import { buildChatRequest, parseExpressChatEvent, ExpressChatEvent } from './expressProtocol';
import { authHeaders } from './triageBackend';

export interface ExpressChatSSEState {
  /** True while a turn (normal or tail-resumed) is actively streaming. */
  streaming: boolean;
  /** True from the start of `sendUserMessage` until a `ready` event arrives.
   *  Daytona sandboxes sleep after idle, so ANY turn — not just the first —
   *  can trigger a 15-30s cold boot; the caller shows a spinner for this. */
  waitingForReady: boolean;
  /** Bumped once a `/tail` resume delivers at least one live event, so the
   *  caller can debounce a `getHistory()` reconcile once streaming settles
   *  (opencode's event bus has no replay — a part that started and finished
   *  entirely during the disconnect gap can be missing even after a
   *  successful tail). A plain counter (not a boolean) so a second resume in
   *  the same mount is also observable via a changed value. */
  reconnectNonce: number;
}

interface UseExpressChatSSEArgs {
  baseUrl: string;
  token: string;
  sessionId: string | null;
  onEvent: (evt: ExpressChatEvent) => void;
  onError?: (message: string) => void;
}

function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

// A turn's stream is expected to emit SOMETHING (an SSE frame, or the `done`
// terminator) at least this often. If express-code silently wedges — a
// sandbox hangs, the connection drops without a close frame — the user's only
// escape today would be `Stop` or reopening the modal, with the composer
// disabled the whole time. Aborting on idle turns that into a normal,
// recoverable error.
const STREAM_IDLE_TIMEOUT_MS = 60_000;

/** Turn a raw fetch/stream failure into a message that actually helps someone
 *  triaging a triage-chat incident, instead of the browser's generic
 *  "Failed to fetch" (a bare network-level failure — DNS, CORS, connection
 *  refused, the sandbox never accepting the connection — carries none of
 *  fetch's own diagnostics) or an undifferentiated status-code string. */
function describeExpressError(err: unknown): string {
  if (err instanceof TypeError) {
    // The browser's fetch() throws a bare TypeError with no status code for
    // any network-level failure — the one case with zero built-in detail to
    // preserve, so replace it outright rather than pass "Failed to fetch"
    // through unexplained.
    return 'Unable to reach the AI triage backend. Check your network connection and try again.';
  }
  if (err instanceof Error && /^express-code chat request failed: (\d+)/.test(err.message)) {
    const status = Number(err.message.match(/(\d+)$/)![1]);
    if (status === 401 || status === 403) {
      return 'Your session has expired or is unauthorized. Try reopening the chat.';
    }
    if (status === 404) {
      return 'This chat session was not found — it may have expired. Try reopening the chat.';
    }
    if (status >= 500) {
      return `The AI triage backend hit a server error (${status}). Try again in a moment.`;
    }
    return `The AI triage backend rejected the request (${status}).`;
  }
  if (err instanceof Error && err.message === 'stream-idle-timeout') {
    return 'The AI triage backend stopped responding. Try again.';
  }
  return err instanceof Error ? err.message : String(err);
}

/**
 * Browser client for express-code's per-turn SSE chat protocol. Unlike
 * veda-ai's persistent WebSocket, a turn here is a single `POST
 * /chat/{sessionId}` request whose response body is an SSE stream — there is
 * no long-lived connection to hold open across turns. What DOES need to
 * survive a drop is a single in-flight TURN: `attachTail()` re-attaches to
 * `GET /chat/{sessionId}/tail` when the previous stream was cut (server-side
 * wall-clock cap, tab throttling, laptop sleep) while opencode kept
 * generating server-side. See docs/superpowers/plans/2026-07-01-triage-
 * express-backend-migration.md (R5) for the full rationale.
 *
 * This hook does NOT own chat message/bubble state — that's `useTriageChat`'s
 * job. It only manages the fetch+SSE lifecycle and forwards parsed events.
 */
export function useExpressChatSSE({
  baseUrl,
  token,
  sessionId,
  onEvent,
  onError,
}: UseExpressChatSSEArgs) {
  const [state, setState] = useState<ExpressChatSSEState>({
    streaming: false,
    waitingForReady: false,
    reconnectNonce: 0,
  });

  // Kept in refs (not deps) so a stale closure from a prior render never
  // fires — same pattern as useVedaChatSocket's onReconnectRef. onEvent in
  // particular is very likely to be a fresh inline callback from the caller's
  // reducer dispatch on every render.
  const onEventRef = useRef(onEvent);
  const onErrorRef = useRef(onError);
  onEventRef.current = onEvent;
  onErrorRef.current = onError;

  // The AbortController for whatever stream (normal turn or tail) is
  // currently open. Only one stream is ever in flight per hook instance —
  // starting a new one aborts the previous, and `abort()` targets this.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      // Don't leave a fetch reader dangling past unmount — there's no
      // equivalent to useVedaChatSocket's "hold the socket for a grace
      // period" here because a turn is server-persisted independent of any
      // client connection (unlike veda-ai's in-memory agent_state), so
      // there's nothing to lose by closing our end immediately.
      controllerRef.current?.abort();
    };
  }, []);

  /**
   * Read one SSE response to completion, feeding each chunk through
   * eventsource-parser and handing every named event to `handleEvent`. The
   * parser callback (not the caller) decides when to abort: `done`/`error`
   * are terminal for express-code's protocol, so we cut the read loop right
   * there rather than waiting for the server to close the socket.
   */
  const readStream = useCallback(
    async (
      controller: AbortController,
      url: string,
      init: RequestInit,
      handleEvent: (evt: ExpressChatEvent) => void,
    ): Promise<void> => {
      // Set right when the idle timer fires, BEFORE `controller.abort()` — a
      // real (or mocked) reader/fetch tied to that signal rejects its own
      // pending promise with an AbortError as a side effect of the abort, and
      // that rejection can win the race below ahead of our own thrown Error
      // (both settle in the same synchronous tick). Without this flag the
      // outer catch's `isAbortError` check would swallow the timeout as an
      // ordinary, silent abort instead of surfacing it. Shared across BOTH
      // the initial connect and the read loop below — a wedge can happen
      // before the server ever sends response headers, not just mid-stream.
      let timedOut = false;
      const withIdleTimeout = <T>(promise: Promise<T>): Promise<T> => {
        // If the timeout branch wins, `controller.abort()` still rejects this
        // same promise a tick later — swallow that secondary rejection so it
        // doesn't surface as unhandled.
        promise.catch(() => {});
        let idleTimer: ReturnType<typeof setTimeout>;
        return Promise.race([
          promise,
          new Promise<never>((_, reject) => {
            idleTimer = setTimeout(() => {
              timedOut = true;
              controller.abort();
              reject(new Error('stream-idle-timeout'));
            }, STREAM_IDLE_TIMEOUT_MS);
          }),
        ]).finally(() => clearTimeout(idleTimer));
      };

      let res: Response;
      try {
        res = await withIdleTimeout(fetch(url, { ...init, signal: controller.signal }));
      } catch (err) {
        if (timedOut) throw new Error('stream-idle-timeout');
        throw err; // a genuine external abort (e.g. Stop before headers) — let the caller's isAbortError check handle it
      }
      if (!res.ok || !res.body) {
        throw new Error(`express-code chat request failed: ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      // eventsource-parser v3's `createParser` takes a callbacks OBJECT (not a
      // bare function like v2) — it throws a TypeError otherwise. `onEvent`
      // receives `{ event?, id?, data }` directly, with no `.type` discriminant
      // to check (unlike the browser EventSource / v2 parser shape).
      const parser = createParser({
        onEvent: (event) => {
          const evt = parseExpressChatEvent(event.event ?? 'message', event.data);
          handleEvent(evt);
          if (evt.kind === 'done' || evt.kind === 'error') controller.abort();
        },
      });
      try {
        while (true) {
          const { done, value } = await withIdleTimeout(reader.read());
          if (done) break;
          parser.feed(decoder.decode(value, { stream: true }));
        }
      } catch (err) {
        if (timedOut) throw new Error('stream-idle-timeout');
        // Our own `controller.abort()` above (on done/error) surfaces here as
        // an AbortError from the in-flight `reader.read()` — expected, not a
        // real failure. Same for an externally-triggered `abort()` call.
        if (!isAbortError(err)) throw err;
      }
    },
    [],
  );

  const sendUserMessage = useCallback(
    (text: string, isFirstTurn: boolean) => {
      if (!sessionId) return;
      // Only one turn's stream is ever open at a time; starting a new one
      // implicitly cancels whatever the previous call left running (defensive
      // — callers are expected to wait for `streaming` to clear first).
      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;

      // Set at the START of every turn, not just the first: Daytona sandboxes
      // sleep after idle, so a cold boot can hit any turn (R3).
      setState((s) => ({ ...s, streaming: true, waitingForReady: true }));

      const url = `${baseUrl}/chat/${sessionId}`;
      readStream(
        controller,
        url,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
          body: JSON.stringify(buildChatRequest(text, isFirstTurn)),
        },
        (evt) => {
          if (evt.kind === 'ready') {
            setState((s) => ({ ...s, waitingForReady: false }));
          } else if (evt.kind === 'error') {
            onErrorRef.current?.(evt.detail);
          }
          onEventRef.current(evt);
        },
      )
        .catch((err) => {
          if (isAbortError(err)) return;
          onErrorRef.current?.(describeExpressError(err));
        })
        .finally(() => {
          // Only react if this is still the controller we care about — a
          // superseded call's promise chain still runs to completion (its
          // AbortError gets swallowed above), but it must not clobber state
          // that a newer, still-running call already owns.
          if (controllerRef.current !== controller) return;
          controllerRef.current = null;
          setState((s) => ({ ...s, streaming: false, waitingForReady: false }));
        });
    },
    [baseUrl, sessionId, token, readStream],
  );

  const attachTail = useCallback((): Promise<{ hadLiveEvents: boolean }> => {
    if (!sessionId) return Promise.resolve({ hadLiveEvents: false });
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    // Local flag, not state: we need the definitive answer synchronously at
    // resolution time to return `{ hadLiveEvents }` to the caller. `done` /
    // `error` / `ignore` don't count — per the backend contract, a tail with
    // nothing in flight closes IMMEDIATELY with a single `done` event and
    // nothing else, so seeing only that (or only `ignore` frames) means there
    // was no live turn to resume.
    let hadLiveEvents = false;

    setState((s) => ({ ...s, streaming: true }));

    const url = `${baseUrl}/chat/${sessionId}/tail`;
    return readStream(controller, url, { headers: authHeaders(token) }, (evt) => {
      if (evt.kind === 'ready') {
        // resumePrompt is documented as non-null ONLY on a genuine /tail
        // resume handshake (see expressProtocol.ts) — forwarded via onEvent
        // below so the caller can show the real prompt instead of a
        // placeholder. Gate `hadLiveEvents` on that field rather than the
        // bare event kind: if express-code ever emitted `ready` on a tail
        // with nothing in flight (a contract violation, but one this hook
        // has no way to detect otherwise), trusting the kind alone would
        // wrongly skip re-seeding a genuinely empty reused session.
        hadLiveEvents = hadLiveEvents || evt.resumePrompt != null;
      } else if (evt.kind === 'error') {
        onErrorRef.current?.(evt.detail);
      } else if (evt.kind !== 'done' && evt.kind !== 'ignore') {
        hadLiveEvents = true;
      }
      onEventRef.current(evt);
    })
      .catch((err) => {
        if (isAbortError(err)) return;
        onErrorRef.current?.(describeExpressError(err));
      })
      .finally(() => {
        // Only react if this is still the controller we care about — a
        // superseded call's promise chain still runs to completion (its
        // AbortError gets swallowed above), but it must not clobber state
        // (including reconnectNonce) that a newer, still-running call
        // already owns.
        if (controllerRef.current !== controller) return;
        controllerRef.current = null;
        setState((s) => ({
          ...s,
          streaming: false,
          // Only a tail that actually resumed a live turn should trigger the
          // caller's debounced history reconcile — a no-op tail (nothing was
          // in flight) has nothing to reconcile.
          reconnectNonce: hadLiveEvents ? s.reconnectNonce + 1 : s.reconnectNonce,
        }));
      })
      .then(() => ({ hadLiveEvents }));
  }, [baseUrl, sessionId, token, readStream]);

  const abort = useCallback(() => {
    if (sessionId) {
      // Fire-and-forget: the UI's abort intent is "stop showing this as
      // in-flight," which the local controller.abort() below satisfies
      // immediately. The server-side abort call is best-effort cleanup so
      // opencode actually stops generating; a failure here shouldn't block
      // or throw into the caller.
      fetch(`${baseUrl}/chat/${sessionId}/abort`, {
        method: 'POST',
        headers: authHeaders(token),
      }).catch(() => {});
    }
    controllerRef.current?.abort();
    controllerRef.current = null;
    setState((s) => ({ ...s, streaming: false, waitingForReady: false }));
  }, [baseUrl, sessionId, token]);

  return useMemo(
    () => ({ ...state, sendUserMessage, attachTail, abort }),
    [state, sendUserMessage, attachTail, abort],
  );
}
